import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Box,
  TableCell as MuiTableCell
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { Delete, Edit, HelpOutline } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableCell from '../../components/TableCell';
import FaqModal from '../../modals/FAQModal';
import { deleteFAQAsync, getFAQAsync } from '../../redux/slices/faqSlice';
import { RootState } from '../../redux/store';
import { FAQ } from '../../resources/types/faqTypes';
import Title from '../../components/Title';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease'
}));

const TableHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: 'transparent',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  '&:first-of-type': {
    paddingLeft: '20px'
  },
  '&:last-of-type': {
    paddingRight: '20px'
  }
}));

const FaqPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFAQAsync());
  }, [dispatch]);

  const faq = useSelector((state: RootState) => state.faq.items);

  const deleteFAQ = async (faqId: string) => {
    if (window.confirm('Видалити?')) {
      await dispatch(deleteFAQAsync(faqId));
      dispatch(getFAQAsync());
    }
  };

  const [faqDialog, setFaqDialog] = useState<{ open: boolean; faq?: FAQ }>({
    open: false
  });

  return (
    <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Title>FAQ</Title>
        <Button
          variant='contained'
          color='primary'
          onClick={() => setFaqDialog({ open: true })}
        >
          Створити
        </Button>
      </Box>
      
      <TableContainer sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableHeaderRow>
              <TableHeaderCell>Тип</TableHeaderCell>
              <TableHeaderCell>Запитання</TableHeaderCell>
              <TableHeaderCell>Відповідь</TableHeaderCell>
              <TableHeaderCell align='right'>Дії</TableHeaderCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {faq.map(item => (
              <StyledTableRow key={item._id}>
                <TableCell>
                  <Tooltip title="FAQ" arrow>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: 'rgba(156, 39, 176, 0.12)',
                        borderRadius: '8px'
                      }}
                    >
                      <HelpOutline sx={{ fontSize: 24, color: '#9C27B0' }} />
                    </Avatar>
                  </Tooltip>
                </TableCell>
                <TableCell>{item?.question?.ua ?? ''}</TableCell>
                <TableCell>{item?.answer?.ua ?? ''}</TableCell>
                <TableCell align='right'>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title='Редагувати'>
                      <IconButton
                        size='medium'
                        onClick={() => setFaqDialog({ open: true, faq: item })}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Видалити'>
                      <IconButton
                        size='medium'
                        onClick={() => deleteFAQ(item._id)}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <FaqModal {...faqDialog} onClose={() => setFaqDialog({ open: false })} />
    </Paper>
  );
};

export default FaqPage;
